import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import DataContext from '../../Contexts/DataContext';

// Styled components for grouped options
const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const GroupItems = styled('ul')({
  padding: 0,
});

/**
 * AccountFilterPanel - Filter accounts by Entity, Department, and Parent Account
 * 
 * @param {function} onApplyFilter - Callback when Apply button is clicked with filtered accounts
 */
const AccountFilterPanel = ({ onApplyFilter }) => {
  const context = useContext(DataContext);

  // State for selections
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  // State for options
  const [entityOptions, setEntityOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);

  // Load initial data
  useEffect(() => {
    if (context.activeProjectAttributes?.entities) {
      setEntityOptions(context.activeProjectAttributes.entities);
    }
  }, [context.activeProjectAttributes?.entities]);

  // Update department options when entities are selected
  useEffect(() => {
    if (selectedEntities.length === 0) {
      setDepartmentOptions([]);
      setSelectedDepartments([]);
      setAccountOptions([]);
      setSelectedAccounts([]);
      return;
    }

    const departments = context.activeProjectAttributes?.departments || [];
    const filteredDepartments = departments.filter(dept =>
      selectedEntities.some(entity => String(entity.entity_id) === String(dept.entityId))
    );

    // Sort departments by entity name to avoid duplicate group headers
    const sortedDepartments = filteredDepartments.sort((a, b) => {
      const entityNameA = a.entityName || `Entity ${a.entityId}`;
      const entityNameB = b.entityName || `Entity ${b.entityId}`;
      return entityNameA.localeCompare(entityNameB);
    });

    setDepartmentOptions(sortedDepartments);
  }, [selectedEntities, context.activeProjectAttributes?.departments]);

  // Update account options when departments are selected
  useEffect(() => {
    if (selectedDepartments.length === 0) {
      setAccountOptions([]);
      setSelectedAccounts([]);
      return;
    }

    const accounts = context.activeProjectAttributes?.accountsMetadata || [];
    const filteredAccounts = accounts.filter(account =>
      selectedEntities.some(entity => String(entity.entity_id) === String(account.entity_id)) &&
      selectedDepartments.some(dept => 
        String(dept.departmentId) === String(account.department_id) && 
        String(dept.entityId) === String(account.entity_id)
      )
    );

    setAccountOptions(filteredAccounts);
  }, [selectedDepartments, selectedEntities, context.activeProjectAttributes?.accountsMetadata]);

  // Handle entity selection
  const handleEntityChange = (newValue) => {
    setSelectedEntities(newValue || []);
  };

  // Handle department selection
  const handleDepartmentChange = (newValue) => {
    setSelectedDepartments(newValue || []);
  };

  // Handle account selection
  const handleAccountChange = (newValue) => {
    setSelectedAccounts(newValue || []);
  };

  // Handle group selection for departments
  const handleDepartmentGroupSelect = (params) => {
    const groupName = params.group;
    
    // Get all departments in this group
    const groupItems = departmentOptions.filter(dept => 
      (dept.entityName || `Entity ${dept.entityId}`) === groupName
    );

    // Check if all items in group are already selected
    const allSelected = groupItems.every(item =>
      selectedDepartments.some(selected => 
        selected.entityId === item.entityId && selected.departmentId === item.departmentId
      )
    );

    if (allSelected) {
      // Deselect all items in group
      setSelectedDepartments(prev =>
        prev.filter(selected =>
          !groupItems.some(item =>
            item.entityId === selected.entityId && item.departmentId === selected.departmentId
          )
        )
      );
    } else {
      // Select all items in group (add only items not already selected)
      const newSelections = [...selectedDepartments];
      groupItems.forEach(item => {
        if (!newSelections.some(s => s.entityId === item.entityId && s.departmentId === item.departmentId)) {
          newSelections.push(item);
        }
      });
      setSelectedDepartments(newSelections);
    }
  };

  // Handle group selection for accounts
  const handleAccountGroupSelect = (params) => {
    const groupName = params.group;
    
    // Get all accounts in this group
    const groupItems = groupedAccountOptions.filter(account => 
      account.parentAccountGroup === groupName
    );

    // Check if all items in group are already selected
    const allSelected = groupItems.every(item =>
      selectedAccounts.some(selected => selected.id === item.id)
    );

    if (allSelected) {
      // Deselect all items in group
      setSelectedAccounts(prev =>
        prev.filter(selected => !groupItems.some(item => item.id === selected.id))
      );
    } else {
      // Select all items in group (add only items not already selected)
      const newSelections = [...selectedAccounts];
      groupItems.forEach(item => {
        if (!newSelections.some(s => s.id === item.id)) {
          newSelections.push(item);
        }
      });
      setSelectedAccounts(newSelections);
    }
  };

  // Handle apply filter
  const handleApply = () => {
    if (onApplyFilter) {
      onApplyFilter(selectedAccounts);
    }
  };

  // Grouped account options by parent account
  const groupedAccountOptions = useMemo(() => {
    const grouped = accountOptions.map(account => ({
      ...account,
      parentAccountGroup: account.parent_account || 'No Parent Account',
    }));
    
    // Sort by parent account group to avoid duplicate headers
    return grouped.sort((a, b) => 
      a.parentAccountGroup.localeCompare(b.parentAccountGroup)
    );
  }, [accountOptions]);

  return (
    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon />
        Filter Accounts
      </Typography>

      <Grid container spacing={2}>
        {/* Entity Selection */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            options={entityOptions}
            getOptionLabel={(entity) => `${entity.entity_id} - ${entity.entity_name}`}
            value={selectedEntities}
            onChange={(event, newValue) => handleEntityChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Entities" placeholder="Choose entities..." />
            )}
          />
        </Grid>

        {/* Department Selection */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            options={departmentOptions}
            getOptionLabel={(dept) => `${dept.departmentId} - ${dept.departmentName}`}
            groupBy={(dept) => dept.entityName || `Entity ${dept.entityId}`}
            value={selectedDepartments}
            onChange={(event, newValue) => handleDepartmentChange(newValue)}
            noOptionsText="Select entities first"
            renderGroup={(params) => (
              <li key={params.key}>
                <GroupHeader onClick={() => handleDepartmentGroupSelect(params)}>
                  {params.group}
                </GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Departments" placeholder="Choose departments..." />
            )}
          />
        </Grid>

        {/* Account Selection */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            options={groupedAccountOptions}
            getOptionLabel={(account) => `${account.gl_key || account.id} - ${account.account_name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id || option.gl_key === value.gl_key}
            groupBy={(account) => account.parentAccountGroup}
            value={selectedAccounts}
            onChange={(event, newValue) => handleAccountChange(newValue)}
            noOptionsText="Select departments first"
            renderOption={(props, account) => {
              const { key, ...restProps } = props;
              return (
                <li key={key} {...restProps}>
                  <strong>{account.gl_key || account.id}</strong> - {account.account_name}
                </li>
              );
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                <GroupHeader onClick={() => handleAccountGroupSelect(params)}>
                  {params.group}
                </GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Accounts" placeholder="Choose accounts..." />
            )}
          />
        </Grid>

        {/* Apply Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center', mr: 2 }}>
              {selectedAccounts.length} account(s) selected
            </Typography>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              onClick={handleApply}
              disabled={selectedAccounts.length === 0}
            >
              Apply Filter
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountFilterPanel;
